import {motion, useReducedMotion} from 'motion/react';

/**
 * Elemento 3D del toolkit flotando: entra con rise + settle y queda en un
 * loop continuo de vaivén (sube/baja, deriva lateral y balanceo). `depth`
 * escala la amplitud: cuanto más "cerca" está el elemento, más se mueve.
 */
export function Floating({
  src,
  alt = '',
  className,
  depth = 1,
  delay = 0,
  drift = 10,
  duration = 5,
}: {
  src: string;
  alt?: string;
  className?: string;
  depth?: number;
  delay?: number;
  drift?: number;
  duration?: number;
}) {
  const reduced = useReducedMotion();
  if (reduced) {
    return <img src={src} alt={alt} className={className} loading="lazy" />;
  }

  // Amplitudes exageradas y escaladas por profundidad.
  const amp = drift * (1 + depth * 0.9);
  const sway = amp * 0.45;
  const tilt = 2.5 + depth * 2.2;
  const loopDelay = delay + 0.9;

  return (
    <motion.div
      className={className}
      initial={{opacity: 0, y: 46, scale: 0.85, rotate: -4}}
      animate={{opacity: 1, y: 0, scale: 1, rotate: 0}}
      transition={{duration: 0.9, delay, ease: [0.16, 1, 0.3, 1]}}
    >
      <motion.div
        className="h-full w-full"
        animate={{
          y: [0, -amp, -amp * 0.35, 0],
          x: [0, sway, -sway * 0.7, 0],
          rotate: [0, tilt, -tilt * 0.65, 0],
          scale: [1, 1.04, 0.98, 1],
        }}
        transition={{
          y: {duration, delay: loopDelay, repeat: Infinity, ease: 'easeInOut'},
          x: {
            duration: duration * 1.45,
            delay: loopDelay,
            repeat: Infinity,
            ease: 'easeInOut',
          },
          rotate: {
            duration: duration * 1.2,
            delay: loopDelay,
            repeat: Infinity,
            ease: 'easeInOut',
          },
          scale: {
            duration: duration * 1.7,
            delay: loopDelay,
            repeat: Infinity,
            ease: 'easeInOut',
          },
        }}
      >
        <img src={src} alt={alt} className="h-full w-full object-contain" draggable={false} />
      </motion.div>
    </motion.div>
  );
}
